import React from 'react';
import { motion } from 'framer-motion';
import { InfoIcon } from 'lucide-react';
import { Button } from './Button';
import { Select } from './Select';
import { TextInput } from './TextInput';
import { type RunningOrderSummary } from './runningOrderOfficeData';
export type SupervisorFilter = 'all' | 'adjusted' | 'standard';
type OrderMatrixTableProps = {
  orders: RunningOrderSummary[];
  view: 'running' | 'completed';
  isLoading: boolean;
  searchQuery: string;
  supervisorFilter: SupervisorFilter;
  startDate: string;
  endDate: string;
  onSearchChange: (value: string) => void;
  onSupervisorFilterChange: (value: SupervisorFilter) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onOpenOrder: (orderId: string) => void;
  onResetFilters: () => void;
};
const supervisorFilterOptions = [{
  value: 'all',
  label: 'Alle Hinweise'
}, {
  value: 'adjusted',
  label: 'Rezeptur angepasst'
}, {
  value: 'standard',
  label: 'Keine Anpassung'
}];
export function OrderMatrixTable({
  orders,
  view,
  isLoading,
  searchQuery,
  supervisorFilter,
  startDate,
  endDate,
  onSearchChange,
  onSupervisorFilterChange,
  onStartDateChange,
  onEndDateChange,
  onOpenOrder,
  onResetFilters
}: OrderMatrixTableProps) {
  const viewLabel = view === 'running' ? 'Aktuell' : 'Abgeschlossen';
  const viewLabelLowercase = view === 'running' ? 'laufenden' : 'abgeschlossenen';
  return <div data-id="element-234">
      <div className="border-b border-border bg-surface px-4 py-4 sm:px-5" data-id="element-235">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_220px_180px_180px] lg:items-end" data-id="element-236">
          <div className="min-w-0" data-id="element-237">
            <TextInput id={`order-search-${view}`} name="orderSearch" type="search" label="" aria-label={`${viewLabel} durchsuchen`} placeholder="Auftrag, Produkt oder Bearbeiter" value={searchQuery} onChange={event => onSearchChange(event.target.value)} data-id="element-238" />
          </div>
          <div className="min-w-0" data-id="element-239">
            <Select id={`order-supervisor-filter-${view}`} name="supervisorFilter" label="" aria-label="Supervisor-Hinweis" options={supervisorFilterOptions} value={supervisorFilter} onChange={event => onSupervisorFilterChange(event.target.value as SupervisorFilter)} data-id="element-240" />
          </div>
          <TextInput id={`order-start-date-${view}`} name="startDate" type="date" label="Von" value={startDate} onChange={event => onStartDateChange(event.target.value)} data-id="element-241" />
          <TextInput id={`order-end-date-${view}`} name="endDate" type="date" label="Bis" value={endDate} onChange={event => onEndDateChange(event.target.value)} data-id="element-242" />
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-243">
        <table className="w-full min-w-[1340px] table-fixed text-left" data-id="element-244">
          <caption className="sr-only" data-id="element-245">
            Fortschrittsmatrix der {viewLabelLowercase} Produktionsaufträge.
            Eine Tabellenzeile öffnen, um das read-only Produktionsprotokoll zu
            prüfen.
          </caption>
          <colgroup data-id="element-246">
            <col className="w-[20%]" data-id="element-247" />
            <col className="w-[12%]" data-id="element-248" />
            <col className="w-[17%]" data-id="element-249" />
            <col className="w-[12%]" data-id="element-250" />
            <col className="w-[16%]" data-id="element-251" />
            <col className="w-[12%]" data-id="element-252" />
            <col className="w-[15%]" data-id="element-253" />
          </colgroup>
          <thead className="border-b border-border bg-surface-subtle" data-id="element-254">
            <tr className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-255">
              <th scope="col" className="px-5 py-4" data-id="element-256">
                Auftrag
              </th>
              <th scope="col" className="px-4 py-4" data-id="element-257">
                Lot
              </th>
              <th scope="col" className="px-4 py-4" data-id="element-258">
                Fortschritt
              </th>
              <th scope="col" className="px-4 py-4" data-id="element-259">
                Bearbeiter
              </th>
              <th scope="col" className="px-4 py-4" data-id="element-260">
                Gestartet
              </th>
              <th scope="col" className="px-4 py-4" data-id="element-261">
                In Produktion
              </th>
              <th scope="col" className="px-4 py-4" data-id="element-262">
                Supervisor
              </th>
            </tr>
          </thead>
          <tbody aria-busy={isLoading} aria-label={isLoading ? `${viewLabel} werden geladen` : viewLabel} data-id="element-263">
            {isLoading ? <OrderMatrixTableSkeleton data-id="element-264" /> : orders.length > 0 ? orders.map((order, index) => {
            const currentLot = Math.min(order.completedLots + 1, order.lots);
            const progress = Math.round(order.completedLots / order.lots * 100);
            return <motion.tr key={order.id} role="button" tabIndex={0} aria-label={`${order.id}, ${order.product} öffnen`} onClick={() => onOpenOrder(order.id)} onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenOrder(order.id);
              }
            }} className="cursor-pointer border-b border-border outline-none last:border-b-0 hover:bg-surface-subtle focus-visible:bg-brand-yellow-light focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-brand-green" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.24,
              delay: 0.06 + index * 0.045,
              ease: 'easeOut'
            }} data-id="element-265">
                    <th scope="row" className="px-5 py-5 align-middle" data-id="element-266">
                      <p className="font-heading text-base text-text-primary" data-id="element-267">
                        {order.id}
                      </p>
                      <p className="mt-1 truncate text-sm text-text-secondary" data-id="element-268">
                        {order.product}
                      </p>
                    </th>
                    <td className="px-4 py-5 align-middle" data-id="element-269">
                      <span className="inline-flex rounded-md bg-brand-yellow-light px-3 py-2 text-sm font-semibold text-text-primary" data-id="element-270">
                        Lot {currentLot} von {order.lots}
                      </span>
                    </td>
                    <td className="px-4 py-5 align-middle" data-id="element-271">
                      <div className="max-w-[190px]" data-id="element-272">
                        <div className="h-1.5 overflow-hidden rounded-full bg-border" role="progressbar" aria-label={`${order.id}: ${progress} Prozent abgeschlossen`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-valuetext={`${order.completedLots} von ${order.lots} Lots abgeschlossen, ${progress} Prozent`} data-id="element-273">
                          <motion.div className="h-full rounded-full bg-success" initial={{
                      width: 0
                    }} animate={{
                      width: `${progress}%`
                    }} transition={{
                      duration: 0.65,
                      delay: 0.18 + index * 0.045,
                      ease: 'easeOut'
                    }} data-id="element-274" />
                        </div>
                        <p className="mt-2 text-xs text-text-secondary" data-id="element-275">
                          {order.completedLots} Lots abgeschlossen · {progress}{' '}
                          %
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-5 align-middle" data-id="element-276">
                      <span className="font-heading text-sm text-text-primary" data-id="element-277">
                        {order.operator}
                      </span>
                    </td>
                    <td className="px-4 py-5 align-middle" data-id="element-278">
                      <time dateTime="2026-07-13" className="whitespace-nowrap text-sm text-text-secondary" data-id="element-279">
                        {order.startedAt}
                      </time>
                    </td>
                    <td className="px-4 py-5 align-middle" data-id="element-280">
                      <span className="whitespace-nowrap font-heading text-sm text-text-primary" data-id="element-281">
                        {order.productionDuration}
                      </span>
                    </td>
                    <td className="px-4 py-5 align-middle" data-id="element-282">
                      {order.supervisorNote === 'Rezeptur angepasst' ? <span className="inline-flex items-center gap-2 text-sm font-semibold text-error" data-id="element-283">
                          <InfoIcon size={15} strokeWidth={2} aria-hidden="true" data-id="element-284" />
                          Rezeptur angepasst
                        </span> : <span className="text-base text-text-secondary" aria-label="Keine Rezepturanpassung" data-id="element-285">
                          −
                        </span>}
                    </td>
                  </motion.tr>;
          }) : <tr data-id="element-286">
                <td colSpan={7} className="px-5 py-10 text-center" data-id="element-287">
                  <p className="font-heading text-base text-text-primary" data-id="element-288">
                    Keine passenden Aufträge gefunden
                  </p>
                  <p className="mt-1 text-sm text-text-secondary" data-id="element-289">
                    Passen Sie die Suche oder den Filter an.
                  </p>
                  <Button type="button" variant="ghost" size="sm" className="mt-3" onClick={onResetFilters} data-id="element-290">
                    Filter zurücksetzen
                  </Button>
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}
function OrderMatrixTableSkeleton() {
  return <>
      {Array.from({
      length: 6
    }, (_, index) => <tr key={index} className="animate-pulse border-b border-border last:border-b-0" aria-hidden="true" data-id="element-291">
            <td className="px-5 py-5" data-id="element-292">
              <div className="h-4 w-36 rounded bg-border" data-id="element-293" />
              <div className="mt-3 h-3 w-52 rounded bg-border" data-id="element-294" />
            </td>
            <td className="px-4 py-5" data-id="element-295">
              <div className="h-9 w-24 rounded-md bg-border" data-id="element-296" />
            </td>
            <td className="px-4 py-5" data-id="element-297">
              <div className="h-1.5 w-40 rounded-full bg-border" data-id="element-298" />
              <div className="mt-3 h-3 w-28 rounded bg-border" data-id="element-299" />
            </td>
            <td className="px-4 py-5" data-id="element-300">
              <div className="h-4 w-20 rounded bg-border" data-id="element-301" />
            </td>
            <td className="px-4 py-5" data-id="element-302">
              <div className="h-4 w-36 rounded bg-border" data-id="element-303" />
            </td>
            <td className="px-4 py-5" data-id="element-304">
              <div className="h-4 w-24 rounded bg-border" data-id="element-305" />
            </td>
            <td className="px-4 py-5" data-id="element-306">
              <div className="h-4 w-28 rounded bg-border" data-id="element-307" />
            </td>
          </tr>)}
    </>;
}